<template>
    <div class="upsell-cart">
        <section class="upsell-cart-header">
            <SaasIcon icon="svgLogoSellsyText" width="118" height="28" />
            <CompassLink
                type="secondary"
                prefixIcon="caret-left"
                size="small"
                label=""
                @click="goToPrevious"
            >
                <span class="typographies-paragraph-medium-bold">{{ $l10n.__('Retour') }}</span>
            </CompassLink>
        </section>

        <template v-if="!loading">
            <!-- LEFT SIDE-->
            <section :class="['upsell-cart-settings', settingClasses]">
                <h1 class="typographies-display-large upsell-cart-settings__heading">
                    {{ title }}
                </h1>

                <!-- ITEMS CONFIG -->
                <ChargebeeUpsellConfigPV3 v-show="step === 0" v-if="getIsPricingVersion3" />
                <ChargebeeUpsellConfig v-show="step === 0" v-else />

                <!-- ITEMS SUMMARY -->
                <ChargebeeUpsellSettingsSummary v-show="step > 0" class="upsell-cart-summary" />
            </section>

            <!-- RIGHT SIDE -->
            <section class="upsell-cart-payment-summary">
                <h2 class="upsell-cart-payment-summary__heading typographies-display-small">
                    {{ paymentSummaryHeading }}
                </h2>
                <!-- PAYMENT SUMMARY-->
                <ChargebeeUpsellPaymentSummary
                    class="upsell-cart-payment-summary__container"
                    :step="step"
                />

                <!-- PAYMENT METHOD -->
                <div v-if="step > 0" class="upsell-cart-payment-summary-method">
                    <span class="typographies-paragraph-medium-bold">
                        {{ $l10n.__('Payé via') }}
                    </span>
                    <div>
                        <div :class="['card', { 'card--is-invalid': hasErrorPaymentMethod }]">
                            <SaasIcon
                                :icon="paymentMethodIcon"
                                :width="24"
                                :height="isSEPAPaymentMethod ? 18 : 16"
                            />
                            <span class="typographies-paragraph-medium-regular">
                                <template v-if="isCardPaymentMethod">
                                    {{ $l10n.__(`finissant par ${getPaymentMethodData.last4}`) }}
                                </template>
                                <template v-else-if="isSEPAPaymentMethod">
                                    {{ $l10n.__('SEPA') }}
                                </template>
                            </span>
                        </div>
                        <CompassButton
                            :label="$t.__('common.actions.edit')"
                            type="tertiary"
                            @click="openCbPortalSection"
                        />
                    </div>
                </div>

                <CompassAlert
                    v-if="hasError"
                    type="error"
                    :description="errorMsg"
                    :showClose="false"
                />
                <!-- BUTTONS -->
                <CompassTooltip
                    :content="
                        $t.__(
                            'pages.account.upsell.ChargebeeUpsellCart.update_subscription_to_continue',
                        )
                    "
                    contentClass="typographies-paragraph-small-regular"
                    :disabled="canNextStep"
                    placement="bottom"
                >
                    <CompassButton
                        class="upsell-cart-payment-summary-confirm__button"
                        :label="nextStepButtonLabel"
                        :disabled="!canNextStep"
                        :loading="getEstimationLoading"
                        @click="nextStep"
                    />
                </CompassTooltip>
                <CompassButton
                    v-if="step > 0"
                    class="upsell-cart-payment-summary-cancel__button"
                    type="secondary"
                    :label="$t.__('common.actions.cancel')"
                    :loading="getEstimationLoading"
                    @click="goToAccountPreferences"
                />
            </section>
        </template>

        <template v-if="displayPreload">
            <preloader :display="true" />
        </template>
    </div>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import ChargebeeUpsellConfig from 'vcomponents/pages/account/upsell/ChargebeeUpsellConfig.vue';
import ChargebeeUpsellConfigPV3 from 'vcomponents/pages/account/upsell/pricingVersion3/ChargebeeUpsellConfigPV3.vue';
import ChargebeeUpsellPaymentSummary from 'vcomponents/pages/account/upsell/ChargebeeUpsellPaymentSummary.vue';
import ChargebeeUpsellSettingsSummary from 'vcomponents/pages/account/upsell/ChargebeeUpsellSettingsSummary.vue';
import ChargebeeUpsellMixin from 'mixins/chargebee/ChargebeeUpsellMixins';
import { fromSnakeToPascal } from 'Helpers/caseConversion';
import { goToUrl } from 'vueDir/helpers';
import ChargebeeUpsellStore from 'stores/chargebee/chargebeeUpsellStore';
import { createNamespacedHelpers } from 'vuex';
const chargebeeUpsellStore = createNamespacedHelpers('chargebeeUpsellStore');
import * as ChargebeeUpsellService from 'api/chargebee/chargebeeService';
import Env from 'Services/Env.js';

import SaasIcon from 'vcomponents/common/SaasIcon.vue';

export default {
    components: {
        SaasIcon,
        Preloader,
        ChargebeeUpsellConfig,
        ChargebeeUpsellConfigPV3,
        ChargebeeUpsellPaymentSummary,
        ChargebeeUpsellSettingsSummary,
    },
    mixins: [ChargebeeUpsellMixin],
    data() {
        return {
            step: 0,
            loading: true,
            displayPreload: true,
            previousAmount: 0,
            paymentIntentResponse: null,
            hasErrorPaymentMethod: false,
        };
    },
    computed: {
        ...chargebeeUpsellStore.mapGetters([
            'getPreviousSubscriptionItems',
            'getSubscriptionItems',
            'getRecurrence',
            'getPaymentMethodData',
            'getChargebeeCatalogue',
            'getEstimationLoading',
            'getSubscriptionId',
            'getSubscriptionIncoming',
            'getSubscriptionInvoicing',
            'getCustomerData',
            'getDiscounts',
            'getDomains',
            'getIsPricingVersion3',
            'getBillingCycles',
        ]),
        currentAmount() {
            return this.getSubscriptionItems.reduce((sum, record) => sum + record.amount, 0);
        },
        hasUpdated() {
            return this.previousAmount !== this.currentAmount;
        },
        hasError() {
            return (this.hasErrorPaymentMethod && this.isLastStep) || this.hasErrorTotalAmount;
        },
        hasErrorTotalAmount() {
            return this.currentAmount < this.previousAmount;
        },
        isLastStep() {
            return this.step > 0;
        },
        title() {
            return this.isLastStep
                ? this.$l10n.__('Évolution de votre abonnement')
                : this.$l10n.__('Faire évoluer mon abonnement');
        },
        isCardPaymentMethod() {
            return this.getPaymentMethodData.hasOwnProperty('card_type');
        },
        isSEPAPaymentMethod() {
            return (
                this.getPaymentMethodData.hasOwnProperty('paymentMethodType') &&
                this.getPaymentMethodData.paymentMethodType === 'direct_debit'
            );
        },
        paymentMethodIcon() {
            let paymentMethodIcon = 'Card';
            if (this.isCardPaymentMethod) {
                paymentMethodIcon = `svg${fromSnakeToPascal(this.getPaymentMethodData.card_type)}`;
            } else if (this.isSEPAPaymentMethod) {
                paymentMethodIcon = 'svgSEPA';
            }

            return paymentMethodIcon;
        },
        paymentSummaryHeading() {
            return this.isLastStep
                ? this.$l10n.__('Détail du paiement')
                : this.$l10n.__('Récapitulatif');
        },
        nextStepButtonLabel() {
            return this.isLastStep
                ? this.$l10n.__('Confirmer l’évolution de mon abonnement')
                : this.$l10n.__('Continuer');
        },
        settingClasses() {
            return this.isLastStep ? 'upsell-cart-settings--is-large' : '';
        },
        hasValidDomains() {
            const hasDomains = this.getSubscriptionItems.some(
                (item) => item.nameKey === 'pack_marketing_custom_domain',
            );
            return (
                !hasDomains || (hasDomains && this.getDomains.some((domain) => domain.isValidated))
            );
        },
        canNextStep() {
            return this.hasUpdated && !this.hasError && this.hasValidDomains;
        },
        errorMsg() {
            return this.hasErrorTotalAmount
                ? this.$l10n.__(
                      `Le montant de votre nouvel abonnement ne peut pas être inférieur à l'ancien montant.`,
                  )
                : this.$l10n.__(
                      `Votre moyen de paiement est invalide ou a expiré. Veuillez en ajouter un nouveau pour procéder au paiement.`,
                  );
        },
    },
    beforeCreate() {
        this.$store.registerModule('chargebeeUpsellStore', ChargebeeUpsellStore);
    },
    async created() {
        try {
            await this.fetchChargebeeCatalogue();
            await this.fetchSubscription();
            this.previousAmount = this.getPreviousSubscriptionItems.reduce(
                (sum, record) => sum + record.amount,
                0,
            );
            if (this.isCardPaymentMethod) {
                this.hasErrorPaymentMethod = this.getPaymentMethodData?.status === 'expired';
            }
        } catch (e) {
            if (e.cause === 'has_scheduled_changes') {
                Utils.relocate('/?_f=accountPreferences&action=subscription');
                return;
            }
            this.callError(e);
        } finally {
            this.loading = false;
            this.displayPreload = false;
            this.scrollFromUrlParams();
        }
        EventBus.on('chargebee:payment:source:updated', this.updateChargebeePaymentSource);
    },
    beforeUnmount() {
        this.$store.unregisterModule('chargebeeUpsellStore');
        window.ChargebeeGlobal.clearChargebeeScript();
        EventBus.off('chargebee:payment:source:updated', this.updateChargebeePaymentSource);
    },
    methods: {
        ...chargebeeUpsellStore.mapActions([
            'fetchChargebeeCatalogue',
            'fetchSubscription',
            'updateCustomerPaymentMethodData',
        ]),
        scrollFromUrlParams() {
            if (this.$route.query.scrollTo) {
                setTimeout(() => {
                    this.scrollIntoViewById(this.$route.query.scrollTo);
                }, 800);
            }
        },
        scrollIntoViewById(id, offset = 0) {
            const elem = document.getElementById(id);
            if (elem) {
                document.getElementById('sellsy_app').scrollTo({
                    top: parseInt(elem.offsetTop) - offset,
                    behavior: 'smooth',
                });
            }
        },
        goToAccountPreferences() {
            goToUrl('/?_f=accountPreferences&action=subscription');
        },
        goToPrevious() {
            this.isLastStep ? (this.step -= 1) : this.goToAccountPreferences();
        },
        nextStep() {
            if (!this.isLastStep) {
                this.step += 1;
            } else {
                this.submitSubscription();
            }
        },
        async tryPaymentIntent() {
            try {
                //testing payment method
                await window.ChargebeeGlobal.initChargebeeScript();
                const responseThreeDSHandler = await this.threeDSecureHandler();
                const responseCreatePaymentIntent = await this.paymentIntentCreation();
                this.paymentIntentResponse = await this.paymentIntent(
                    responseCreatePaymentIntent.data.payment_intent,
                    responseThreeDSHandler,
                );
                this.hasErrorPaymentMethod = this.getPaymentMethodData?.status !== 'valid';
            } catch (e) {
                this.hasErrorPaymentMethod = true;
            }
        },
        threeDSecureHandler() {
            const cbInstance = window.Chargebee.init({ site: Env.get('chargebee').site ?? '' });

            return new Promise((resolve, reject) => {
                cbInstance
                    .load3DSHandler()
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((err) => reject(err));
            });
        },
        paymentIntentCreation() {
            return new Promise((resolve, reject) => {
                ChargebeeUpsellService.createPaymentIntent({
                    reference_id: this.getCustomerData.payment_method.reference_id,
                    gateway_account_id: this.getCustomerData.payment_method.gateway_account_id,
                    amount: Math.round(this.getSubscriptionInvoicing.heading.price),
                })
                    .then((response) => resolve(response))
                    .catch((err) => reject(err));
            });
        },
        paymentIntent(paymentIntent, threeDSHandler) {
            return new Promise((resolve, reject) => {
                threeDSHandler.setPaymentIntent(paymentIntent);
                threeDSHandler
                    .handleCardPayment()
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((err) => reject(err));
            });
        },
        async submitSubscription() {
            this.displayPreload = true;
            try {
                await this.tryPaymentIntent();
                await ChargebeeUpsellService.updateSubscription({
                    id: this.getSubscriptionId,
                    addons: this.getSubscriptionItems,
                    discounts: this.getDiscounts,
                    paymentIntent: this.paymentIntentResponse,
                    billingCycles: this.getBillingCycles,
                });
                localStorage.setItem('chargebee_upsell_update_success', true);
                localStorage.setItem('chargebee_upsell_domains', JSON.stringify(this.getDomains));
                this.goToAccountPreferences();
            } catch (err) {
                console.error(err);
                if (!this.hasErrorPaymentMethod) {
                    this.callError();
                }
            } finally {
                this.displayPreload = false;
            }
        },
        async openCbPortalSection() {
            await window.ChargebeeGlobal.openCbPortalSection('PAYMENT_SOURCES');
        },
        async updateChargebeePaymentSource() {
            try {
                const { data: response } = await ChargebeeUpsellService.fetchSubscription(true);

                this.hasErrorPaymentMethod = false;
                this.updateCustomerPaymentMethodData({
                    card: response.card,
                    customer: response.customer,
                });
                if (response?.card) {
                    this.tryPaymentIntent();
                } else {
                    this.paymentIntentResponse = null;
                }
            } catch (err) {
                console.error(err);
                this.callError();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.upsell-cart {
    display: grid;
    grid-template-columns: 1fr 472px;
    grid-template-areas:
        'header payment'
        'settings payment';

    &-header {
        display: flex;
        grid-area: header;
        flex-direction: column;
        align-items: start;
        gap: var(--spacing-0);
        svg {
            color: var(--colors-component-background-illustration-brand-secondary-blue);
        }
    }
    &-settings {
        display: flex;
        grid-area: settings;
        flex-direction: column;
        place-self: center;
        width: 560px;
        padding: var(--spacing-6) 0;

        &__heading {
            margin: 0 0 var(--spacing-4);
        }
        &--is-large {
            width: 616px;
        }
    }

    &-payment-summary {
        position: fixed;
        display: flex;
        grid-area: payment;
        flex-direction: column;
        top: 0;
        right: 0;
        height: 100%;
        width: 504px;
        padding: var(--spacing-6);
        background: var(--colors-common-background-subtle);

        &__heading {
            margin: 0 0 var(--spacing-4);
        }

        &-method {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-1);
            padding: var(--spacing-1) var(--spacing-3);
            margin-bottom: var(--spacing-4);
            background: var(--colors-common-background-default);
            border-radius: 4px;

            .card {
                align-items: center;
                flex-direction: row;
                gap: var(--spacing-0);
                &--is-invalid {
                    opacity: 0.5;
                }
            }

            & > div {
                display: flex;
                align-items: center;
                gap: var(--spacing-2);
            }
        }

        &-cancel__button {
            margin-top: var(--spacing-3);
        }

        :deep(.compass-tooltip) {
            width: 100%;
            .compass-button {
                width: 100%;
            }
        }
    }
}
</style>
